import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * H1_Desc_4 Component
 * Displays a heading (H1) and description with support for multiple paragraphs
 * Centered layout with clean, simple design
 * The "4" indicates support for multiple description sections/paragraphs
 */
const H1Desc4 = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    component_name = "H1_Desc_4",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
  }, [id, title, description]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
    });
    setIsEditing(false);
  };

  // Helper function to split description into paragraphs
  const getParagraphs = (text) => {
    if (!text) return [];
    // Split by double newlines or single newlines if double newlines don't exist
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
    if (paragraphs.length === 0 && text.trim()) {
      // If no double newlines, split by single newlines
      return text.split(/\n/).filter((p) => p.trim());
    }
    return paragraphs;
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component h1-desc-4-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Heading & Description (4 Sections)</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Heading (H1)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter heading (e.g., Hey *|FNAME|*!)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description. Use double line breaks (Enter twice) to separate paragraphs."
            rows={8}
          />
          <small className="text-muted">
            Tip: Use double line breaks (press Enter twice) to create separate paragraphs.
          </small>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const paragraphs = getParagraphs(localDescription);

  return (
    <>
      {!previewMode && (
        <div className="email-component h1-desc-4 mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tbody>
          <tr>
            <td
              align="center"
              style={{
                padding: "32px 16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tbody>
                  {/* Heading (H1) */}
                  {localTitle && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <h1
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            margin: "0 0 16px 0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localTitle}
                        </h1>
                      </td>
                    </tr>
                  )}

                  {/* Description Paragraphs */}
                  {paragraphs.length > 0 ? (
                    paragraphs.map((paragraph, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td
                            align="center"
                            style={{
                              paddingBottom: index < paragraphs.length - 1 ? "16px" : "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "16px",
                                color: primaryColor || "#333333",
                                lineHeight: "1.6",
                                margin: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                textAlign: "center",
                              }}
                            >
                              {paragraph.trim()}
                            </p>
                          </td>
                        </tr>
                        {/* Divider line between paragraphs (except after last) */}
                        {index < paragraphs.length - 1 && (
                          <tr>
                            <td
                              align="center"
                              style={{
                                padding: "16px 0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                }}
                              >
                                <tr>
                                  <td
                                    style={{
                                      height: "1px",
                                      backgroundColor: "#e0e0e0",
                                      lineHeight: "1px",
                                      fontSize: "1px",
                                    }}
                                  >
                                    &nbsp;
                                  </td>
                                </tr>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : localDescription ? (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            color: primaryColor || "#333333",
                            lineHeight: "1.6",
                            margin: "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localDescription}
                        </p>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default H1Desc4;

